import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAuthStore from '@/store/authStore';
import * as authService from '@/services/authService';

const inputClass =
  'w-full rounded-xl border border-white/15 bg-surface/10 px-4 py-3 text-sm text-white outline-none transition placeholder:text-white/40 focus:border-accent focus:bg-surface/15 focus:ring-2 focus:ring-accent/20';
const labelClass = 'mb-1.5 block text-xs font-bold uppercase tracking-widest text-white/50';

export default function LogistaLoginPage() {
  const navigate = useNavigate();
  const loginStore = useAuthStore((s) => s.login);

  const [form, setForm] = useState({ login: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authService.login({ username: form.login, password: form.password });
      const { token, user, player } = res.data.data;
      if (user.role !== 'partner') {
        setError('Esta conta não é de logista. Usa o login correto para o teu perfil.');
        return;
      }
      loginStore(token, user, player);
      navigate('/logista/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Email ou password incorretos.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="grid min-h-full place-items-center bg-[#0f1c2e] px-4 py-8">
      {/* Subtle background glow */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-accent/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-accent/30 blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm">
        <div className="mb-8 text-center">
          <img src="/branding/logo-hakkencity.webp" alt="Hakken City" className="mx-auto h-16 w-auto" />
          <h1 className="mt-4 font-serif text-2xl font-black text-white">Portal do Logista</h1>
          <p className="mt-1 text-sm text-white/50">Entra na tua conta para gerir a tua loja</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-surface/[0.07] p-6 shadow-2xl backdrop-blur-sm">
          <form onSubmit={submit} className="grid gap-4">
            <div>
              <label className={labelClass}>Email</label>
              <input
                type="text"
                required
                autoComplete="email"
                value={form.login}
                onChange={(e) => setForm({ ...form, login: e.target.value })}
                className={inputClass}
                placeholder="email@exemplo.com"
              />
            </div>

            <div>
              <label className={labelClass}>Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className={`${inputClass} pr-14`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-white/40 hover:text-white/80 transition"
                  tabIndex={-1}
                >
                  {showPassword ? 'Ocultar' : 'Ver'}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2.5 rounded-xl border border-red-400/20 bg-red-500/10 px-4 py-3">
                <span className="mt-0.5 text-sm">⚠️</span>
                <p className="text-sm font-semibold text-red-300">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-1 w-full rounded-xl bg-accent py-3.5 text-sm font-bold text-white shadow-lg transition hover:bg-accent active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? 'A entrar...' : 'Entrar'}
            </button>
          </form>
        </div>

        <div className="mt-5 space-y-2 text-center">
          <p className="text-sm text-white/50">
            Ainda não tens conta?{' '}
            <Link to="/logista/register" className="font-bold text-accent hover:underline">
              Registar grátis
            </Link>
          </p>
          <p className="text-sm text-white/30">
            <Link to="/" className="hover:text-white/60 transition">← Voltar ao início</Link>
          </p>
        </div>
      </div>
    </main>
  );
}
